import { AliasType, PaginationSort } from '../gql/graphql';
import { getPaginatedNodes, toPagination } from './utils';
import { useEffect, useMemo } from 'react';

import { graphql } from '../gql';
import { useQuery } from '@apollo/client';

export interface Option {
  label: string;
  value: string;
  status?: string;
  isInactive?: boolean;
}
export const GET_ENTITIES_BROKER_AGENCIES_OPTIONS = graphql(`
  query getBrokerAgenciesOptions(
    $pagination: PaginationRequest
    $filters: EntityFilterInput!
    $aliasFilters: AliasFilterInput!
    $sort: PaginationSort
  ) {
    entities(pagination: $pagination, filters: $filters, sort: $sort) {
      edges {
        cursor
        node {
          agency
          uuid
          referenceId
          status
          aliases(filters: $aliasFilters) {
            edges {
              cursor
              queryCursor
              node {
                uuid
                name
                type
                sources
                primary
              }
            }
          }
        }
      }
      pageInfo {
        totalResults
        hasNextPage
        hasPreviousPage
        pageNumber
        pageSize
      }
    }
  }
`);
export function useGetEntitiesBrokeragesOptions({
  pageNumber = 0,
  pageSize = 15,
  uuid,
  isAgency = true,
  isParent = undefined,
  depthSearch = 1,
  sort,
  enabled,
  brokerageName,
}: {
  pageNumber?: number;
  pageSize?: number;
  depthSearch?: number;
  uuid?: string;
  isAgency?: boolean;
  isParent?: boolean;
  sort?: PaginationSort;
  enabled?: boolean;
  brokerageName?: string;
}) {
  const variables = {
    pagination: toPagination(pageNumber, pageSize),
    filters: {},
    aliasFilters: {},
    sort: sort || { key: 'PRIMARY_LEGAL_NAME', order: 'ASC' },
  };
  if (isParent !== undefined) {
    variables.filters = {
      agency: isAgency,
      entityUuid: uuid,
      parents: isParent,
      depthSearch: depthSearch,
    };
  } else {
    variables.filters = {
      agency: isAgency,
      entityUuid: uuid,
    };
  }

  if (brokerageName) {
    variables.filters = { ...variables.filters, brokerageName: brokerageName };
  }
  if (brokerageName) {
    variables.aliasFilters = { name: brokerageName, source: 'PRIMARY' };
  } else {
    variables.aliasFilters = { source: 'PRIMARY' };
  }
  const { loading, error, data, refetch, fetchMore } = useQuery(
    GET_ENTITIES_BROKER_AGENCIES_OPTIONS,
    {
      variables,
      skip: !enabled,
      fetchPolicy: 'network-only',
    }
  );

  const brokerages = useMemo(() => {
    if (!data || !data.entities || !data.entities.edges) return [];

    const brokerageOptions: Option[] = [];
    getPaginatedNodes(data.entities).forEach((edge) => {
      const legalNameAliases = getPaginatedNodes(edge.aliases).filter(
        ({ type, primary }) => primary && type === AliasType.LegalName
      );

      const tradingNameAliases = getPaginatedNodes(edge.aliases).filter(
        ({ type, primary }) => primary && type === AliasType.TradingName
      );

      const isInactive =
        edge.status === 'INACTIVE' || edge.status === 'OUT_OF_BUSINESS';
      let baseName = '';

      if (legalNameAliases.length > 0 && legalNameAliases[0]?.name?.trim()) {
        baseName = legalNameAliases[0]?.name ?? edge.referenceId ?? 'N/A';
      } else if (tradingNameAliases.length > 0) {
        baseName = tradingNameAliases[0]?.name ?? edge.referenceId ?? 'N/A';
      } else {
        baseName = edge.referenceId ?? 'N/A';
      }

      const displayName = isInactive ? `${baseName} (Inactive)` : baseName;

      brokerageOptions.push({
        value: edge?.uuid ?? '-',
        label: displayName,
        status: edge.status ?? undefined,
        isInactive,
      });
    });
    return brokerageOptions.sort((a, b) => a.label.localeCompare(b.label));
  }, [data]);

  const pageInfo = data?.entities?.pageInfo;
  const hasNextPage = pageInfo?.hasNextPage ?? false;

  useEffect(() => {
    if (enabled) {
      refetch();
    }
  }, [enabled, refetch]);

  return {
    error,
    loading,
    data,
    brokerages,
    refetch,
    fetchMore,
    hasNextPage,
    pageInfo,
  };
}
